/**
 * Pilihan sumbu kurva untuk layar lintas-sumur (Crossplot, Histogram, Pickett).
 *
 * Dulu pilihannya hanya kurva milik sumur di dalam lingkup. Begitu lingkup itu
 * satu sumur yang tidak punya kurva yang sedang dipilih — sumur Teapot Dome
 * tanpa PHIE, atau tanpa kurva sama sekali — daftarnya kosong atau tidak memuat
 * pilihan yang tersimpan, dropdown-nya menampilkan "—", dan layar terkunci pada
 * pasangan yang tidak bisa digambar. Sekarang pilihannya **semua kurva di
 * proyek**, ditambah yang sedang terpilih, dan yang tidak ada di lingkup tetap
 * bisa dipilih dengan keterangan bahwa ia tidak ada di sumur-sumur itu.
 */

import { useEffect, useRef, useState } from "react";

export interface CurveOption {
  value: string;
  label: string;
}

/** Keterangan pada kurva yang ada di proyek tetapi tidak di lingkup. */
export const NOT_IN_SCOPE = "not in these wells";

export function curveOptions(
  project: readonly string[],
  inScope: readonly string[] | undefined,
  keep: readonly (string | null | undefined)[] = [],
): CurveOption[] {
  const names = new Set<string>();
  for (const name of [...project, ...(inScope ?? []), ...keep]) {
    if (name) names.add(name.toUpperCase());
  }
  const here = inScope ? new Set(inScope.map((name) => name.toUpperCase())) : null;
  return [...names]
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      value: name,
      label: here && !here.has(name) ? `${name} · ${NOT_IN_SCOPE}` : name,
    }));
}

/**
 * Nilai terakhir yang terdefinisi. Jawaban plot hilang sebentar setiap kali
 * sumbunya berpindah; daftar pilihan yang ikut hilang selama itu membuat
 * dropdown berkedip kosong tepat di tangan orang yang sedang memakainya.
 */
export function useLastDefined<T>(value: T | undefined): T | undefined {
  const last = useRef<T | undefined>(value);
  if (value !== undefined) last.current = value;
  return last.current;
}

/** Pasangan sumbu yang dicoba berurutan saat sumbu terpilih tidak ada di lingkup. */
export const AXIS_PAIRS: readonly (readonly [string, string])[] = [
  ["NPHI", "RHOB"], ["TNPH", "RHOZ"], ["NPHI", "RHOZ"], ["TNPH", "RHOB"], ["CNL", "RHOB"],
  ["GR", "RT"], ["GR", "ILD"], ["GR", "RILD"], ["GR", "LLD"], ["GR", "RDEP"], ["GR", "RES"],
  ["GR", "RHOB"], ["GR", "NPHI"], ["DT", "RHOB"], ["GR", "DT"], ["GR", "SP"], ["SP", "RT"],
];

/** Kurva yang lazim, untuk memilih pasangan bila tidak satu pun pasangan di atas lengkap. */
const COMMON = ["GR", "RT", "NPHI", "RHOB", "DT", "SP", "ILD", "LLD", "RHOZ", "TNPH", "CALI", "PEF"];

function rank(names: readonly string[]): string[] {
  const score = (name: string) => {
    const i = COMMON.indexOf(name);
    return i < 0 ? COMMON.length : i;
  };
  return [...names].sort((a, b) => score(a) - score(b) || a.localeCompare(b));
}

/**
 * Sumbu pengganti untuk lingkup yang tidak memuat sumbu terpilih, atau `null`
 * bila keduanya ada (tidak ada yang perlu diganti) atau lingkupnya tanpa kurva.
 *
 * Sumbu yang ada dipertahankan dan hanya pasangannya yang dicari; yang
 * diutamakan pasangan petrofisika yang lazim, lalu kurva yang lazim.
 */
export function adaptAxes(
  available: readonly string[],
  x: string,
  y: string,
): [string, string] | null {
  const here = new Set(available.map((name) => name.toUpperCase()));
  const hasX = here.has(x.toUpperCase());
  const hasY = here.has(y.toUpperCase());
  if ((hasX && hasY) || here.size === 0) return null;
  const pairs = AXIS_PAIRS.filter(([a, b]) => here.has(a) && here.has(b));
  if (hasX) {
    const partner = pairs.find(([a, b]) => a === x.toUpperCase() || b === x.toUpperCase());
    const other = partner
      ? partner[0] === x.toUpperCase() ? partner[1] : partner[0]
      : rank([...here].filter((n) => n !== x.toUpperCase()))[0];
    return other ? [x, other] : null;
  }
  if (hasY) {
    const partner = pairs.find(([a, b]) => a === y.toUpperCase() || b === y.toUpperCase());
    const other = partner
      ? partner[0] === y.toUpperCase() ? partner[1] : partner[0]
      : rank([...here].filter((n) => n !== y.toUpperCase()))[0];
    return other ? [other, y] : null;
  }
  if (pairs.length) return [pairs[0][0], pairs[0][1]];
  const ranked = rank([...here]);
  return [ranked[0], ranked[1] ?? ranked[0]];
}

/**
 * Satu kurva pengganti bila yang terpilih tidak ada di lingkup: yang pertama
 * dari `preferred` yang ada, lalu kurva lazim. `null` bila tidak perlu diganti.
 */
export function adaptOne(
  available: readonly string[],
  current: string,
  preferred: readonly string[] = [],
): string | null {
  const here = new Set(available.map((name) => name.toUpperCase()));
  if (here.size === 0 || here.has(current.toUpperCase())) return null;
  return preferred.find((name) => here.has(name.toUpperCase())) ?? rank([...here])[0] ?? null;
}

/**
 * Jalankan `adapt` sekali untuk tiap lingkup baru, begitu jawabannya tiba.
 *
 * Dipicu oleh jawaban (`data` yang baru), bukan oleh berubahnya lingkup: pada
 * render tempat lingkup berganti, `data` masih jawaban lingkup lama, dan
 * menyesuaikan sumbu dengan daftar kurva sumur yang baru saja ditinggalkan
 * justru memilih sumbu yang salah. Lingkup yang sama tidak disesuaikan dua
 * kali, supaya kurva yang sengaja dipilih orang — meskipun tidak ada di sumur
 * itu — tidak dibatalkan di tangannya.
 */
export function useAdaptOnNewScope(
  data: unknown,
  loading: boolean,
  scopeKey: string,
  adapt: () => void,
): void {
  const done = useRef<string | null>(null);
  const adaptRef = useRef(adapt);
  adaptRef.current = adapt;
  useEffect(() => {
    if (loading || data == null || done.current === scopeKey) return;
    done.current = scopeKey;
    adaptRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dipicu jawaban baru saja
  }, [data, loading]);
}

/**
 * Kurva pilihan orang (tersimpan) dan kurva yang sedang dipakai (efektif).
 *
 * Penyesuaian per lingkup mengubah yang efektif saja. Kalau ia menimpa yang
 * tersimpan, satu sumur yang hanya punya GR dan RW memindahkan GR–PHIE
 * selamanya: sumur berikutnya yang punya PHIE tidak akan kembali ke sana.
 * Memilih dari dropdown mengubah keduanya.
 */
export function usePreferredCurve(
  stored: [string, (value: string) => void],
): { value: string; preferred: string; choose: (value: string) => void; use: (value: string) => void } {
  const [preferred, setPreferred] = stored;
  const [value, setValue] = useState(preferred);
  return {
    value,
    preferred,
    choose: (next: string) => {
      setPreferred(next);
      setValue(next);
    },
    use: setValue,
  };
}
